import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { Location } from "../api/models";
import { getLocationsApi, LocationsResponse } from "../api/locationApi";
import { useMsalAuth, getToken } from "../authConfig";
import { useMsal } from "@azure/msal-react";

interface LocationContextType {
    locations: Location[];
    assignedLocationIds: string[];
    userLocations: Location[];
    isLoading: boolean;
    error: string | null;
    refreshLocations: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [locations, setLocations] = useState<Location[]>([]);
    const [assignedLocationIds, setAssignedLocationIds] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const client = useMsalAuth ? useMsal().instance : undefined;

    const refreshLocations = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            const response: LocationsResponse = await getLocationsApi(token);

            setLocations(response.locations || []);
            setAssignedLocationIds(response.assigned_location_ids || []);
        } catch (err) {
            console.error("Failed to load locations:", err);
            setError(err instanceof Error ? err.message : "Failed to load locations");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (client || !useMsalAuth) {
            refreshLocations();
        }
    }, [client]);

    const userLocations = useMemo(() => {
        if (assignedLocationIds.length === 0) {
            return [];
        }
        return locations.filter(location => assignedLocationIds.includes(location.id));
    }, [locations, assignedLocationIds]);

    return (
        <LocationContext.Provider
            value={{
                locations,
                assignedLocationIds,
                userLocations,
                isLoading,
                error,
                refreshLocations
            }}
        >
            {children}
        </LocationContext.Provider>
    );
};

export const useLocations = () => {
    const context = useContext(LocationContext);
    if (context === undefined) {
        throw new Error("useLocations must be used within a LocationProvider");
    }
    return context;
};
